import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledLabel } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";

import { capitalize } from "@helpers/general";
import { getUuidUser } from "@helpers/getUuidUser";
import { store } from "@components/VerticalLayout/store";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

import { useFetchGlobal } from "../../../../../api/hooks";

import { RootState, AppDispatch } from "../../../../../redux/store";
import {
    setGlobalDataIsUpdated,
    setIsLoading,
} from "../../../../../redux/slices/fidelitySlice";

import { editFidelityForAllShops } from "./editFidelity";

type Props = {
    isModalOpened: boolean;
    setIsModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
    setIsEdited: (v: boolean) => void;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
    dataFidelity: FidelityRow;
    associated: FidelityRow[];
    isAddFiscalYearClicked: boolean;
    setIsAddFiscalYearClicked: React.Dispatch<React.SetStateAction<boolean>>;
    setIsValidateButtonEnabled: React.Dispatch<React.SetStateAction<boolean>>;
};

export function ConfirmationModificationMessageWrapper({
    isModalOpened,
    setIsModalOpened,
    setIsEdited,
    setDataIsUpdated,
    dataFidelity,
    associated,
    isAddFiscalYearClicked,
    setIsAddFiscalYearClicked,
    setIsValidateButtonEnabled,
}: Props) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const globalDataIsUpdated = useSelector(
        (s: RootState) => s.fidelity.globalDataIsUpdated
    );
    const dispatch = useDispatch<AppDispatch>();
    const uuidUser = getUuidUser();

    const handleValidate = async () => {
        setIsModalOpened(!isModalOpened);
        await editFidelityForAllShops({
            associated,
            dataFidelity,
            userID,
            franchiseID,
            operatorID,
            uuidUser,
            fetchGlobal,
            t,
        });
        dispatch(setGlobalDataIsUpdated(!globalDataIsUpdated));
        setDataIsUpdated(true);
        setIsAddFiscalYearClicked(!isAddFiscalYearClicked);
        setIsEdited(false);
        dispatch(setIsLoading(false));
    };

    const handleClose = () => {
        setIsModalOpened(!isModalOpened);
        setIsAddFiscalYearClicked(false);
        setIsValidateButtonEnabled(true);
        setIsEdited(false);
    };

    return (
        <StyledModal toggle={true} isOpen={isModalOpened} centered>
            <ModalHeader className="text-capitalize" toggle={handleClose}>
                <span
                    style={{
                        fontFamily: "Segoe UI Semibold",
                        color: "#2b2828",
                        fontSize: "20px",
                    }}
                >
                    {capitalize(t("Alert"))}
                </span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={handleClose}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={handleValidate}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
